import React, { useMemo, useState } from "react";
import { Alert, Box, Button, TextField } from "@mui/material";
import { Link, useHistory, useLocation } from "react-router-dom";

import { setNetworkPortalToken, validateNetworkPortalInviteToken, verifyNetworkPortalAccount } from "../../services/coreNetworkPortal";
import TalentAuthLayout from "./TalentAuthLayout";

const TALENT_VERIFY_IMAGE_URL = "https://images.pexels.com/photos/8117473/pexels-photo-8117473.jpeg";

export default function TalentVerifyAccount() {
  const history = useHistory();
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get("token") || "", [location.search]);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  async function handleSubmit(event) {
    event.preventDefault();
    try {
      setLoading(true);
      setError("");
      const result = await verifyNetworkPortalAccount(token, password);
      if (!result?.accessToken) {
        throw new Error("Verification did not return a session.");
      }
      setNetworkPortalToken(result.accessToken);
      setMessage("Account verified.");
      setTimeout(() => history.replace("/talent"), 800);
    } catch (verifyError) {
      setError(verifyError?.response?.data?.message || verifyError.message || "Unable to verify account.");
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    let active = true;
    async function validateToken() {
      if (!token) return;
      try {
        await validateNetworkPortalInviteToken(token);
      } catch (validationError) {
        if (active) {
          setError(validationError?.response?.data?.message || validationError.message || "Invalid verification token.");
        }
      }
    }
    validateToken();
    return () => {
      active = false;
    };
  }, [token]);

  return (
    <TalentAuthLayout imageUrl={TALENT_VERIFY_IMAGE_URL} imagePosition="center 44%">
      <Box component="form" onSubmit={handleSubmit}>
        <header>
          <span className="talent-auth-kicker">Talent Portal</span>
          <h1 className="talent-auth-heading">Activate Talent Account</h1>
          <p className="talent-auth-copy">
            Create the password for your global Talent portal account. You will use this same login across authorized Elite Core instances.
          </p>
        </header>
        <Box aria-live="polite">
          {!token ? <Alert className="talent-auth-alert" severity="error">Missing activation token.</Alert> : null}
          {message ? <Alert className="talent-auth-alert" severity="success">{message}</Alert> : null}
          {error ? <Alert className="talent-auth-alert" severity="error">{error}</Alert> : null}
        </Box>
        <div className="talent-auth-fields">
          <TextField
            label="Password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            fullWidth
            autoComplete="new-password"
          />
          <Button className="talent-auth-submit" type="submit" variant="contained" disabled={loading || !token}>
            {loading ? "Activating..." : "Activate Account"}
          </Button>
        </div>
        <nav className="talent-auth-links" aria-label="Talent account navigation">
          <Button component={Link} to="/talent/login" size="small">Back to login</Button>
        </nav>
      </Box>
    </TalentAuthLayout>
  );
}
